import { HTML_TAG_NAMES } from './html-tag-names';

/**
 * Every dangerous pattern needs one of these characters. `&` covers entities
 * that could hide one. Text without any of them skips all the work below.
 */
const MAY_BE_DANGEROUS = /[<&:=(]/;

/**
 * An entity, as a browser reads it in text. Numeric ones and the legacy named
 * ones (`&lt`, `&amp`) work without the `;`. The name group is greedy:
 * `&ltiframe` is checked as a full name first, then as `&lt` + `iframe`.
 */
const HTML_ENTITY = /&(?:#(\d+);?|#x([0-9a-f]+);?|([a-z][a-z0-9]*)(;?))/gi;

/**
 * Named entities that can spell markup or a dangerous pattern once decoded.
 * Decoding is only used to look for hidden markup, so names outside this list
 * (`&copy;`, `&eacute;`) can stay encoded — they cannot spell a tag.
 */
const NAMED_ENTITIES: ReadonlyMap<string, string> = new Map([
  ['lt', '<'],
  ['LT', '<'],
  ['gt', '>'],
  ['GT', '>'],
  ['amp', '&'],
  ['AMP', '&'],
  ['quot', '"'],
  ['QUOT', '"'],
  ['apos', "'"],
  ['sol', '/'],
  ['bsol', '\\'],
  ['colon', ':'],
  ['semi', ';'],
  ['lpar', '('],
  ['rpar', ')'],
  ['equals', '='],
  ['excl', '!'],
  ['num', '#'],
  ['period', '.'],
  ['comma', ','],
  ['grave', '`'],
  ['plus', '+'],
  ['lsqb', '['],
  ['rsqb', ']'],
  ['lbrack', '['],
  ['rbrack', ']'],
  ['lcub', '{'],
  ['rcub', '}'],
  ['lbrace', '{'],
  ['rbrace', '}'],
  ['Tab', '\t'],
  ['NewLine', '\n'],
  ['nbsp', ' '],
]);

/**
 * The HTML5 legacy names a browser decodes with no `;`, cut down to the ones
 * in NAMED_ENTITIES. The rest of the legacy set (`&copy`, `&eacute`) decode
 * to letters and signs that cannot spell markup. Longest first.
 */
const LEGACY_NAMES = ['QUOT', 'quot', 'nbsp', 'AMP', 'amp', 'LT', 'lt', 'GT', 'gt'];

const MAX_CODE_POINT = 0x10ffff;

/** Tab, LF and CR: a URL parser drops these anywhere, even inside `javascript`. */
const URL_IGNORED = String.raw`[\t\n\r]*`;

/** `javascript:` with the tab, CR and LF a URL parser drops between letters. */
function schemePattern(scheme: string): RegExp {
  return new RegExp([...scheme].join(URL_IGNORED) + String.raw`\s*:`, 'gi');
}

/** A strip rule yields the `[start, end)` ranges it wants removed. */
type StripRule = (text: string) => Iterable<readonly [number, number]>;

function* regexRanges(pattern: RegExp, text: string): Generator<readonly [number, number]> {
  for (const match of text.matchAll(pattern)) yield [match.index, match.index + match[0].length];
}

const regexRule =
  (pattern: RegExp): StripRule =>
  (text) =>
    regexRanges(pattern, text);

/** `<name` or `</name`, then whitespace, `/` or `>`. Only the opener is matched. */
const TAG_START = /<\/?([a-zA-Z][a-zA-Z0-9-]*)(?=[\s/>])/g;

/**
 * A real tag: a TAG_START whose name is in HTML_TAG_NAMES, up to the next `>`.
 * Text content is kept. The scan resumes right after each opener, so a
 * rejected `<y …>` cannot hide a real tag inside it (`x<y <script>`). The next
 * `>` is cached, so the scan stays linear even with no `>` at all.
 */
function* realTagRanges(text: string): Generator<readonly [number, number]> {
  let nextGt = -1;
  for (const match of text.matchAll(TAG_START)) {
    if (!HTML_TAG_NAMES.has(match[1].toLowerCase())) continue;
    if (nextGt < match.index) nextGt = text.indexOf('>', match.index);
    if (nextGt === -1) return;
    yield [match.index, nextGt + 1];
  }
}

const NON_TAG_RULES: readonly StripRule[] = [
  regexRule(schemePattern('javascript')),
  regexRule(schemePattern('vbscript')),
  regexRule(/data\s*:\s*(?:text\/html|text\/javascript|image\/svg\+xml)[^;]*/gi),
  regexRule(/expression\s*\(/gi),
  regexRule(/\bon\w+\s*=/gi),
];

const STRIP_RULES: readonly StripRule[] = [realTagRanges, ...NON_TAG_RULES];

/**
 * Fail-closed rules. A `<` run before a letter, `/`, `!` or `?` is what every
 * tag, comment and processing instruction starts with. `:`, `=` and `(` are
 * in every non-tag rule. Removing them cannot form a new match.
 */
const TAG_OPENER: StripRule = function* (text) {
  // A greedy `<+` run with no lookahead stays linear on a long run of `<`.
  for (const [start, end] of regexRanges(/<+/g, text)) {
    if (/[a-z/!?]/i.test(text.charAt(end))) yield [start, end];
  }
};
const NON_TAG_TRIGGER = regexRule(/[:=(]/g);

// Stripping can expose a new pattern (e.g. `<scr<b>ipt>`), so repeat until stable.
// Past this many passes the input is hostile, and we fail closed instead.
const MAX_STRIP_PASSES = 5;

/**
 * One piece of the input. `text` is what a browser would read. `raw` is what
 * the user typed. For plain text both are the same character. For an entity
 * `raw` is `&lt;` and `text` is `<`.
 */
interface Unit {
  readonly text: string;
  readonly raw: string;
}

function fromCodePoint(code: number): string {
  return code > 0 && code <= MAX_CODE_POINT ? String.fromCodePoint(code) : '�';
}

function decodeEntity(match: RegExpMatchArray): Unit | null {
  const [entity, dec, hex, name, semicolon] = match;
  if (dec) return { text: fromCodePoint(Number.parseInt(dec, 10)), raw: entity };
  if (hex) return { text: fromCodePoint(Number.parseInt(hex, 16)), raw: entity };
  const full = semicolon ? NAMED_ENTITIES.get(name) : undefined;
  if (full !== undefined) return { text: full, raw: entity };
  const legacy = LEGACY_NAMES.find((legacyName) => name.startsWith(legacyName));
  return legacy ? { text: NAMED_ENTITIES.get(legacy) ?? '', raw: `&${legacy}` } : null;
}

function pushPlain(units: Unit[], text: string): void {
  for (const ch of text) units.push({ text: ch, raw: ch });
}

function plainUnits(value: string): Unit[] {
  const units: Unit[] = [];
  pushPlain(units, value);
  return units;
}

/**
 * Split the input into units, decoding each entity once. Pure string work,
 * so it runs the same with or without a DOM.
 */
function toUnits(value: string): Unit[] {
  const units: Unit[] = [];
  let last = 0;
  for (const match of value.matchAll(HTML_ENTITY)) {
    const entity = decodeEntity(match);
    if (!entity) continue;
    pushPlain(units, value.slice(last, match.index));
    units.push(entity);
    // A legacy match covers only `&lt` of `&ltiframe`; `iframe` stays plain text.
    last = match.index + entity.raw.length;
  }
  pushPlain(units, value.slice(last));
  return units;
}

const textOf = (units: readonly Unit[]) => units.map((unit) => unit.text).join('');
const rawOf = (units: readonly Unit[]) => units.map((unit) => unit.raw).join('');

function hasMatch(text: string, rules: readonly StripRule[]): boolean {
  return rules.some((rule) => !rule(text)[Symbol.iterator]().next().done);
}

/** Drop every unit whose decoded text starts inside a match of the rule. */
function removeMatches(units: readonly Unit[], rule: StripRule): Unit[] {
  const text = textOf(units);
  const dropped = new Uint8Array(text.length);
  for (const [start, end] of rule(text)) dropped.fill(1, start, end);
  const kept: Unit[] = [];
  let offset = 0;
  for (const unit of units) {
    if (!dropped[offset]) kept.push(unit);
    offset += unit.text.length;
  }
  return kept;
}

const stripPass = (units: readonly Unit[]) =>
  STRIP_RULES.reduce((kept, rule) => removeMatches(kept, rule), [...units]);

/**
 * Called when stripping has not settled after MAX_STRIP_PASSES. Removes the
 * characters every dangerous pattern needs, so nothing can be left behind.
 * Trigger characters go first: removing them can never form a tag opener.
 */
function failClosed(units: readonly Unit[]): Unit[] {
  const noTags = removeMatches(units, TAG_OPENER);
  if (!hasMatch(textOf(noTags), NON_TAG_RULES)) return noTags;
  return removeMatches(removeMatches(noTags, NON_TAG_TRIGGER), TAG_OPENER);
}

/** Strip on the decoded text until nothing changes, or fail closed. */
function stripUnits(units: readonly Unit[]): Unit[] {
  let working = [...units];
  for (let pass = 0; pass < MAX_STRIP_PASSES; pass++) {
    const next = stripPass(working);
    // Rules only remove, so the same length means nothing changed.
    if (next.length === working.length) return working;
    working = next;
  }
  return stripPass(working).length === working.length ? working : failClosed(working);
}

/**
 * Sanitize user input by stripping real HTML tags and dangerous patterns.
 *
 * Returns PLAIN TEXT, never entity-escaped HTML. The result goes back into an
 * input's value, and React escapes it on render (NPJB-838).
 *
 * - `<`, `>` and `&` stay as typed unless they form a real tag
 *   (`x<y and y>z`, `R&D size<Enterprise > SMB` are unchanged)
 * - Real HTML/SVG/MathML tags are stripped but their text content is kept
 * - Dangerous patterns (javascript:, on* handlers, data: URIs) are removed
 * - Entities are decoded only to look for hidden markup, so `&lt;script&gt;`
 *   is stripped. Output rule: the rules run on the decoded text, and every
 *   piece they keep is written back exactly as typed. An entity is never
 *   decoded in the output; it is either kept as typed or removed with the
 *   markup it formed. `5 &lt; 6 &lt;b&gt;x` gives `5 &lt; 6 x`.
 * - Deeply nested markup that does not settle in MAX_STRIP_PASSES fails
 *   closed: every `<` before a letter, `/`, `!` or `?` is removed.
 */
export function sanitizeInput(value: string): string {
  if (!value || !MAY_BE_DANGEROUS.test(value)) return value;

  let result = rawOf(stripUnits(toUnits(value)));
  // What is kept must not form a pattern either: in the typed text, or once its
  // entities are decoded (`&l<b>t;script&gt;` spells `&lt;script&gt;` after the `<b>` goes).
  for (let pass = 0; pass < MAX_STRIP_PASSES; pass++) {
    if (hasMatch(result, STRIP_RULES)) result = rawOf(stripUnits(plainUnits(result)));
    else if (hasMatch(textOf(toUnits(result)), STRIP_RULES)) {
      result = rawOf(stripUnits(toUnits(result)));
    } else return result;
  }
  // Still not settled: drop every `&` so nothing can decode, then fail closed.
  return rawOf(failClosed(plainUnits(result.replaceAll('&', ''))));
}

/**
 * Sanitize a URL search parameter value.
 * Returns null if input is null (for searchParams.get() compatibility).
 */
export function sanitizeUrlParam(value: string | null): string | null {
  if (value === null) return null;
  return sanitizeInput(value);
}

/**
 * Validate that a path is a safe relative URL for redirects.
 * Prevents open redirect attacks via javascript:, data:, external URLs.
 */
export function isRelativePath(value: string): boolean {
  if (!value?.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.startsWith('/\\')) return false;
  // Reject paths with a colon before the first slash segment (e.g., /javascript:alert(1))
  const afterSlash = value.slice(1);
  const colonIndex = afterSlash.indexOf(':');
  const slashIndex = afterSlash.indexOf('/');
  if (colonIndex !== -1 && (slashIndex === -1 || colonIndex < slashIndex)) return false;
  return true;
}
